import { generateXLSX, XLSXRow } from '@retail/products/utils';
import dayjs from 'dayjs';
import { ConditionsNew } from '@retail/calculus/types';
import { useAppTFunction } from '@retail/app/i18n';
import { max } from 'lodash';
import { PriceSuggestionPriority } from '@retail/products/types';

const conditionCsvData = ['MgItem-nummer', 'Varugrupp', 'Rabattgrupp', 'GyldigFra', 'GTIN'];
const markupCsvData = ['DG', 'Salgsfaktor'];

const conditionColumnOrder = [
  'mgItemNumber',
  'groupNumber',
  'discountGroupCode',
  'validFrom',
  'gtinCode',
];
const markupColumnOrder = ['coverageDegree', 'salesFactor'];

export const useSalesPriceConditionExport = (conditions: ConditionsNew[]) => {
  const t = useAppTFunction();

  const getMarkupAsRows = () => {
    const markupRows: XLSXRow[] = [];

    const activeMarkups = conditions.filter((condition) => !condition.markupCondition?.isInherited);

    activeMarkups.forEach((markup) => {
      const row: Record<string, string | number | null> = {
        mgItemNumber: markup.bindingType === 'MG_SUPPLIER_ITEM' ? markup.bindingTargetId : null,
        groupNumber: ['OVERGRUPPE', 'HOVEDGRUPPE', 'VAREGRUPPE'].includes(markup.bindingType)
          ? markup.bindingTargetId
          : null,
        discountGroupCode: markup.bindingType === 'DISCOUNT_GROUP' ? markup.bindingTargetId : null,
        validFrom: markup.markupCondition?.validFrom
          ? dayjs(markup.markupCondition?.validFrom).format('YYYY-MM-DD')
          : null,
        gtinCode:
          markup.bindingTargetItem?.packageType !== 'PRIMARY'
            ? markup.bindingTargetItem?.gtinCode ?? null
            : null,
        coverageDegree: markup.markupCondition?.coverageDegree ?? null,
        salesFactor: markup.markupCondition?.salesFactor?.toFixed(5) ?? null,
      };

      const columnOrder = [...conditionColumnOrder, ...markupColumnOrder];
      const rowValues = columnOrder.map((key) => row[key]);
      markupRows.push(rowValues);
    });

    return markupRows;
  };

  const exportMarkupAsXLSX = async () => {
    const data: XLSXRow[] = [[...conditionCsvData, ...markupCsvData]];
    const updatedData = [...data, ...getMarkupAsRows()];

    try {
      generateXLSX({
        rows: updatedData,
        fileName: `${t('markup.markup')}-${dayjs().format('YYYY-MM-DD')}`,
      });
    } catch (error) {
      console.error('Error exporting XLSX:', error);
    }
  };

  const getSalesPriceAsRows = (longestPrioList: PriceSuggestionPriority[]) => {
    const salesPriceRows: XLSXRow[] = [];

    const prioFields = (condition: ConditionsNew) =>
      condition.priceSourceCondition?.priorities.reduce<Record<string, string | number | null>>(
        (acc, prio) => {
          const fieldName = `priority${prio.priority}`;
          acc[fieldName] = prio.priceSuggestionType;
          return acc;
        },
        {}
      );
    const priorityColumns = longestPrioList.map((prio) => {
      return `priority${prio.priority}`;
    });

    conditions.forEach((condition) => {
      const row: Record<string, string | number | null> = {
        mgItemNumber:
          condition.bindingType === 'MG_SUPPLIER_ITEM' ? condition.bindingTargetId : null,
        groupNumber: ['OVERGRUPPE', 'HOVEDGRUPPE', 'VAREGRUPPE'].includes(condition.bindingType)
          ? condition.bindingTargetId
          : null,
        discountGroupCode:
          condition.bindingType === 'DISCOUNT_GROUP' ? condition.bindingTargetId : null,
        validFrom: condition.markupCondition?.validFrom
          ? dayjs(condition.markupCondition?.validFrom).format('YYYY-MM-DD')
          : null,
        gtinCode:
          condition.bindingTargetItem?.packageType !== 'PRIMARY'
            ? condition.bindingTargetItem?.gtinCode ?? null
            : null,
        ...prioFields(condition),
        coverageDegree: condition.markupCondition?.coverageDegree ?? null,
        salesFactor: condition.markupCondition?.salesFactor?.toFixed(5) ?? null,
        inheritedMarkup: condition.markupCondition?.isInherited ? 'JA' : null,
      };

      const columnOrder = [
        ...conditionColumnOrder,
        ...priorityColumns,
        ...markupColumnOrder,
        'inheritedMarkup',
      ];

      const rowValues = columnOrder.map((key) => row[key]);
      salesPriceRows.push(rowValues);
    });

    return salesPriceRows;
  };

  const exportSalesPriceAsXLSX = async () => {
    const longestPrioList =
      max(conditions.map((cond) => cond.priceSourceCondition?.priorities)) ?? [];

    const priorityColumnNames = longestPrioList?.map((prio) => {
      return `Priority ${prio.priority}`;
    });

    const data: XLSXRow[] = [
      [...conditionCsvData, ...priorityColumnNames, ...markupCsvData, 'Arvet påslag'],
    ];

    const updatedData = [...data, ...getSalesPriceAsRows(longestPrioList)];

    try {
      generateXLSX({
        rows: updatedData,
        fileName: `${t('app.routes.salesPriceCondition')}-${dayjs().format('YYYY-MM-DD')}`,
      });
    } catch (error) {
      console.error('Error exporting XLSX:', error);
    }
  };

  return {
    exportMarkupAsXLSX,
    exportSalesPriceAsXLSX,
  };
};
